<template>
	<b-container fluid class="p-0 m-0 no-gutters">
		<b-row class="p-0 m-0 no-gutters">
			<timeline class="w-100">
				<!-- <timeline-title>Dates d'enregistrement</timeline-title> -->
				<timeline-item v-for="version in versionsHistory" v-bind:key="version.Version" bg-color="transparent" class="py-3">
					<b-row @click="loadVersion(version.Version, true)" role="button" class="p-0 m-0 no-gutters">
						<b-col cols="11" class="p-0 m-0 no-gutters" align-self="center">
							<!-- Affiche un gros point vert pour la version sélectionnée -->
							<div v-if="version.Version === selectedVersionId" class="timeline-circle border-secondary bg-secondary" style="height: 14px; width: 14px; top: 0px; left: -35px;" />
							<!-- Affiche un petit point gris pour les versions non sélectionnées -->
							<div v-else class="timeline-circle border-gray bg-gray" />

							<!-- Utilise le filtre formatDate déclaré dans App.vue pour formater la date. 'L' spécifie le format de date long selon la culture du navigateur (en, fr, ... ==> cf https://momentjs.com/) -->
							<span :class="version.Version === selectedVersionId ? 'font-weight-bold': 'font-weight-normal'">
								<!-- S'il s'agit de la version courante qui n'a pas de date de fin fixée alors on affiche le texte 'aujourd'hui' -->
								 <!-- Today : Aujourd'hui -->
								<span v-if="version.Version == lastVersionId">{{$t('Today')}}</span>
								<span v-else>{{ version.EndDate | formatDate('L') }}</span>
							</span>
						</b-col>
						<b-col v-if="$store.getters['CheckAction']('Delete', 'ObjectSheet')" cols="1" class="p-0 m-0 no-gutters" align-self="center">
							<!-- Bouton de suppression de la version (pas affiché s'il s'agit de la dernière) -->
							<b-icon v-if="version.Version !== lastVersionId"  @click.stop="deleteVersion(version.Version)" icon="x-lg" role="button" class="text-danger text-center border-0" />
						</b-col>
					</b-row>
				</timeline-item>
			</timeline>
		</b-row>
		<b-row v-if="$store.getters['CheckAction']('Add', 'ObjectSheet')" class="pb-2 mx-0">
			<!-- AddDate : Ajouter une date -->
			<b-link v-b-modal.versionDateSelectorModal class="text-center w-100 text-secondary font-weight-bold">{{$t('AddDate')}}</b-link>
		</b-row>
		<!-- History : Historique -->
		<b-modal id="versionDateSelectorModal" v-model="displayDatePickerModal" centered :title="$t('History')">
			<!-- NewDate : Nouvelle date -->
			<b-form-group
				:label="$t('NewDate')"
				label-cols-sm="4"
				label-for="versionDateSelector"
				label-align-sm="left">
				<b-input-group id="versionDateSelector">
					<!-- @todo: gérer le format de la date selon que la culture soit française ou anglaise -->
					 <!-- SelectDatePlaceholder : Veuillez sélectionner une date -->
					<b-form-datepicker :date-format-options="{ day: '2-digit', month: '2-digit', year: 'numeric' }"
						:max="new Date()" locale="fr" start-weekday="1"
						value-as-date v-model="versionDate" :placeholder="$t('SelectDatePlaceholder')" :disabled="loading" />
				</b-input-group>
			</b-form-group>
			<template #modal-footer="{ cancel }">
				<!-- Cancel : Annuler -->
				<b-button @click="cancel()" variant="outline-secondary" pill>{{$t('Cancel')}}</b-button>
				<!-- Archive : Historiser -->
				<b-button @click="createNewVersion()" variant="secondary" pill :disabled="loading"><b-icon icon="clock-history" />{{$t('Archive')}}</b-button>
			</template>
		</b-modal>
	</b-container>
</template>

<script lang="ts">
	import { Component, Prop, PropSync, Vue } from "vue-property-decorator";
	import { Getter } from "vuex-class";
	import { Timeline, TimelineItem, TimelineTitle } from 'vue-cute-timeline';
	import 'vue-cute-timeline/dist/index.css';
	import VersionHistory from "@/types/VersionHistory";
	import SaveModeEnum from "@/enums/SaveModeEnum";
	import moment from "moment";
	import FormDraft from "@/types/fields/FormDraft";
	import locales from "./locales/VersionSelector.json";

	/** Composant de sélection de version avec un rendu sous forme de ligne de vie */
	@Component({
		components: {
			Timeline,
			TimelineItem,
			TimelineTitle
		},
		i18n: { messages: locales }
	})
	export default class VersionSelector extends Vue {
		@Getter('ObjectStore/GetVersionId')
		private selectedVersionId!: number;

		/**  Liste des formulaires de la version en cours */
		@PropSync('Forms')
		public forms!: FormDraft[];

		//#region Fenêtre modale de sélection d'une nouvelle date de version
		private displayDatePickerModal: boolean = false;
		private versionDate: Date = new Date();
		//#endregion Fenêtre modale de sélection d'une nouvelle date de version

		/**  Liste des historiques de versions */
		@Getter('ObjectStore/GetVersionsHistory')
		private versionsHistory!: VersionHistory[];

		/** Chargement en cours */
		private loading: boolean = false;

		/** 
		 * Dernière version de l'objet
		 * @hack : Getter appelé plusieurs fois dans un v-for du template mais vue.js renvoi le résultat en cache qui ne change pas pendant cette boucle sans appeler le contenu du getter 🙂
		 */
		@Getter('ObjectStore/GetCurrentVersionId')
		private lastVersionId!: number;

		/** Chargement de la version sélectionnée */
		private async loadVersion(versionId: number, displayMessage: boolean = false) {
			console.debug("VersionSelector - loadVersion(versionId : " + versionId + ", displayMessage: " + displayMessage + ")");
			
			// Envoi un évènement à ObjectDisplaying.vue pour afficher overlay (masque) d'attente de chargement
			this.$emit('DisplayLoadingOverlay');

			await this.$store.dispatch("ObjectStore/LoadVersion", { versionId: versionId }).then(() => {
				// Mise à jour des forms locale d'ObjectDisplaying
				this.forms = this.$store.getters['ObjectStore/GetForms'];
				/** Indique à ObjectDisplaying de mettre à jour le formulaire chargé à partir des données obtenues par la Prop Forms de VersionSelector */
				this.$emit('UpdateForm');

				if (displayMessage) {
					this.$emit('HideVersionSelector');
					this.$bvToast.toast(this.$t('VersionLoaded').toString(), { // "La version a été chargée avec succès."
						title: this.$t('History').toString(), // Historique
						autoHideDelay: 5000,
						variant: "success",
						solid: true,
						appendToast: true,
						toaster: "b-toaster-top-center"
					});
				}
			}).catch(err => {
				console.error(err);
				this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // Une erreur s'est produite.
					title: this.$t('Error').toString(), // Erreur
					autoHideDelay: 10000,
					variant: "danger",
					solid: true,
					appendToast: true,
					toaster: "b-toaster-top-center"
				});
			})
			.finally(() => {
				// Masque l'overlay
				this.$emit('HideLoadingOverlay');
			});
		}

		/** Création d'une nouvelle version */
		private createNewVersion() {
			// Check si la version existe déjà
			let verIndex = this.versionsHistory.findIndex(x => moment(x.EndDate).format('DD/MM/YYYY') == moment(this.versionDate).format('DD/MM/YYYY') )
			if (verIndex == -1) {
				this.loading = true;

				this.$store.dispatch("ObjectStore/Save", { forms: this.forms, localSaveMode: SaveModeEnum.NewVersion, versionDate: this.versionDate}).then(response => {
					if (response) {
						// Chargement de la fiche objet de la nouvelle version d'objet
						//this.loadVersion(this.lastVersionId);

						this.$bvToast.toast(this.$t('VersionCreated').toString(), { // La version a été créée avec succès.
							title: this.$t('Save').toString(), // Enregistrement
							autoHideDelay: 5000,
							variant: "success",
							solid: true,
							appendToast: true,
							toaster: "b-toaster-top-center"
						});
					}
				}).catch(err => {
					this.$emit('HideLoadingOverlay');
					console.error(err);
					this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // Une erreur s'est produite.
					title: this.$t('Error').toString(), // Erreur
						autoHideDelay: 10000,
						variant: "danger",
						solid: true,
						appendToast: true,
						toaster: "b-toaster-top-center"
					});
				})
				.finally(() => {
					// Ferme la modale de sélection d'une date
					this.displayDatePickerModal = false;
					this.loading = false;
				});
			}
			else {
				this.$bvToast.toast(this.$t('VersionExistsWarning').toString(), { // 'Une version existe déjà à cette date ! Veuillez sélectionner une autre date.'
					title: this.$t('Warning').toString(), // Avertissement
					autoHideDelay: 5000,
					variant: "warning",
					solid: true,
					appendToast: true,
					toaster: "b-toaster-top-center"
				});
			}
		}

		/** Suppression d'une version
		 * @param versionId : Id de version à supprimer
		 * @todo : retrouver l'Id de la version sélectionnée lorsqu'une version est supprimée ? (⚠⚠⚠ : la dernière version d'objet existante (version courante) est systématiquement chargée après suppression d'une version car les Id de version ne sont plus les mêmes après suppression d'une version...)
		 */
		private deleteVersion(versionId: number) {
			console.debug("VersionSelector - deleteVersion(versionId: %d) - this.selectedVersionId : %d", versionId, this.selectedVersionId );

			this.$bvModal.msgBoxConfirm(this.$t('DeleteVersionConfirmation').toString(), { // 'Voulez-vous vraiment supprimer cette version ? Cette action sera irréversible.'
				title: this.$t('DeleteVersionTitle').toString(), okVariant: 'danger', okTitle: this.$t('Yes').toString(), cancelTitle: this.$t('No').toString(), hideHeaderClose: false, centered: true
			}).then(value => {
				if (value) {
					this.$store.dispatch("ObjectStore/DeleteVersion", { versionId: versionId }).then(() => {
						console.debug("VersionSelector - deleteVersion - this.loadVersion(this.lastVersionId : %d);", this.lastVersionId);
						// Chargement de la dernière version
						this.loadVersion(this.lastVersionId);

						this.$bvToast.toast( this.$t('VersionDeleted').toString(), { // 'La version a été supprimée avec succès.'
							title: this.$t('DeleteVersion').toString(), // "Suppression version"
							autoHideDelay: 5000,
							variant: "success",
							solid: true,
							appendToast: true,
							toaster: "b-toaster-top-center"
						});
					})
					.catch(err => {
						console.error(err);
						this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // Une erreur s'est produite.
						title: this.$t('Error').toString(), // Erreur
							autoHideDelay: 10000,
							variant: "danger",
							solid: true,
							appendToast: true,
							toaster: "b-toaster-top-center"
						});
					});
				}
			});
		}
	}
</script>

<style scoped>
	:deep(.timeline-item) {
		margin-top: 0em !important;
		margin-left: 0px !important;
		padding-left: 28px !important;
		padding-top: 1em !important;
		padding-bottom: 1em !important;
	}

	:deep(.timeline-item:hover) {
		background-color: var(--secondary-bg-subtle);
	}
</style>